import React, {useState, useEffect, useCallback, useRef} from 'react';
import {
    RiMenuLine,
    RiCloseLine,
    RiArrowDownSLine,
    RiArrowUpSLine,
} from 'react-icons/ri';
import {Link, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import LanguageSwitcher from '../../common/LanguageSwitcher';
import { getLangFromPathname, stripLangPrefix, withLangPrefix } from '../../../i18n/pathUtils';
import './Header.css';
import HeaderLogo from '../../../assets/images/Header/logo.png';

const Header = () => {
    const {t} = useTranslation();
    const location = useLocation();
    // Normalize a path so encoded slugs (curaçao, en-dashes) compare equal.
    const normalizePath = (p) => {
        if (!p) return '';
        let out = p;
        try {
            out = decodeURIComponent(p);
        } catch (e) {
            out = p;
        }
        out = out.toLowerCase();
        out = stripLangPrefix(out);
        return out.replace(/\/+$/, '') || '/';
    };
    const currentPath = normalizePath(location.pathname);
    const isLinkActive = (link) => normalizePath(link) === currentPath;
    const isAnyActive = (links) => links.some((l) => isLinkActive(l.link));
    const lang = getLangFromPathname(location.pathname);
    const lp = (p) => withLangPrefix(lang, p);
    const [menuOpen, setMenuOpen] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null); // для десктопа
    const [activeCategory, setActiveCategory] = useState(null); // для десктоп-сложных меню
    const [isMobile, setIsMobile] = useState(false);

    // мобильные аккордеоны
    const [openMobileSection, setOpenMobileSection] = useState(null); // aboutcompany / services / jurisdictions
    const [openMobileSubcategory, setOpenMobileSubcategory] = useState(null); // BlogDetails категории внутри services/jurisdictions

    const headerRef = useRef(null);
    // Track which category tab holds the current route, so opening a complex
    // dropdown lands on it (and its active link is immediately visible).
    const servicesActiveCatRef = useRef('gaming-license');
    const jurisdictionsActiveCatRef = useRef('jurisdictions');

    // Определяем мобильное устройство
    useEffect(() => {
        const checkMobile = () => {
            const isNowMobile = window.innerWidth < 1118;
            setIsMobile(isNowMobile);

            // Если ушли с мобилки на десктоп — закрываем мобильное меню/аккордеоны
            if (!isNowMobile) {
                setMenuOpen(false);
                setOpenMobileSection(null);
                setOpenMobileSubcategory(null);
            }
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    const toggleMenu = useCallback(() => {
        setMenuOpen((prev) => {
            const next = !prev;
            if (!next) {
                // при закрытии меню чистим моб. состояния
                setOpenMobileSection(null);
                setOpenMobileSubcategory(null);
            }
            return next;
        });
    }, []);

    const toggleDropdown = useCallback(
        (key) => {
            if (openDropdown === key) {
                setOpenDropdown(null);
                setActiveCategory(null);
            } else {
                setOpenDropdown(key);
                if (key === 'services' || key === 'jurisdictions') {
                    const firstCategory =
                        key === 'services'
                            ? servicesActiveCatRef.current
                            : jurisdictionsActiveCatRef.current;
                    setActiveCategory(firstCategory);
                }
            }
        },
        [openDropdown]
    );

    const closeAll = useCallback(() => {
        setMenuOpen(false);
        setOpenDropdown(null);
        setActiveCategory(null);
        setOpenMobileSection(null);
        setOpenMobileSubcategory(null);
    }, []);

    const scrollToTopAndCloseMenu = () => {
        window.scrollTo(0, 0);
        closeAll();
    };

    const handleClickOutside = useCallback(
        (event) => {
            // If the clicked node was removed from the DOM by a re-render before
            // this handler ran (e.g. the dropdown arrow icon swapping between the
            // down/up variants), it's no longer inside the header even though the
            // click originated there — so it must not be treated as an outside click.
            if (event.target && !event.target.isConnected) {
                return;
            }
            if (headerRef.current && !headerRef.current.contains(event.target)) {
                closeAll();
            }
        },
        [closeAll]
    );

    useEffect(() => {
        document.addEventListener('click', handleClickOutside);
        return () => document.removeEventListener('click', handleClickOutside);
    }, [handleClickOutside]);

    // Блокируем скролл body при открытом моб. меню
    useEffect(() => {
        if (menuOpen && isMobile) {
            document.body.classList.add('menu-open');
        } else {
            document.body.classList.remove('menu-open');
        }

        return () => {
            document.body.classList.remove('menu-open');
        };
    }, [menuOpen, isMobile]);

    const renderDropdownArrow = (isOpen) =>
        isOpen ? (
            <RiArrowUpSLine className="dropdown-arrow"/>
        ) : (
            <RiArrowDownSLine className="dropdown-arrow"/>
        );

    // Данные для категорий Services
    const servicesCategories = [
        {
            id: 'gaming-license',
            name: 'Gaming License',
            links: [
                {name: 'Gaming License Services', link: '/gaming-license-services'},
                {
                    name: 'iGaming & Gambling License – Western Sahara',
                    link: '/igaming-gambling-western-sahara',
                },
                {
                    name: 'Gaming License – Mwali (Comoros)',
                    link: '/gaming-license-mwali',
                },
                {
                    name: 'Gaming License – Anjouan (Comoros)',
                    link: '/gaming-license-anjouan',
                },
                {
                    name: 'Gibraltar iGaming License',
                    link: '/gibraltar-igaming-license',
                },
                {
                    name: 'Gaming & iGaming License – Malta (MGA)',
                    link: '/gaming-igaming-license-malta',
                },
                {
                    name: 'Gaming & Betting License – Cyprus',
                    link: '/gaming-betting-license–cyprus',
                },
                {
                    name: 'Gaming License – Panama',
                    link: '/gaming-license–panama',
                },
                {
                    name: 'Curaçao Gaming License',
                    link: '/curaçao-gaming-license',
                },
                {
                    name: 'Gaming & iGaming License – Seychelles',
                    link: '/gaming-igaming-license–seychelles',
                },
                {
                    name: 'Gaming & Betting License – Saint Lucia',
                    link: '/gaming-betting-license–saint-lucia',
                },
                {
                    name: 'Gaming License Tuvalu',
                    link: '/gaming-license–tuvalu',
                },
                {
                    name: 'Ghana Gaming License',
                    link: '/gaming-license–ghana',
                },
                {
                    name: 'Gaming License – Nauru',
                    link: '/gaming-license-nauru',
                },
            ],
        },
        {
            id: 'forex-brokerage',
            name: 'Forex & Brokerage License',
            links: [
                {
                    name: 'Brokerage & Forex Licenses',
                    link: '/brokerage-forex-licenses',
                },
                {
                    name: 'Forex & Brokerage License in Western Sahara',
                    link: '/forex-brokerage-license-western-sahara',
                },
                {
                    name: 'Western Sahara MetaTrader 4 Brokerage License',
                    link: '/western-sahara-metatrader-4-brokerage-license',
                },
                {
                    name: 'Western Sahara MetaTrader 5 Brokerage License',
                    link: '/western-sahara-metatrader-5-brokerage-license',
                },
                {
                    name: 'Forex & Brokerage License – Mwali (Comoros)',
                    link: '/forex-brokerage-license-mwali',
                },
                {
                    name: 'Brokerage/FX & Clearing House License – Anjouan (Comoros)',
                    link: '/brokerage-license-anjouan',
                },
                {
                    name: 'Brokerage & Forex License – Malta (MFSA)',
                    link: '/brokerage-forex-license-malta',
                },
                {
                    name: 'Forex & Brokerage License – Cyprus (CySEC / CIF)',
                    link: '/forex-brokerage-license-cyprus',
                },
                {
                    name: 'Securities Dealer License – Seychelles',
                    link: '/securities-dealer-license-seychelles',
                },
                {
                    name: 'Forex & Brokerage – St. Vincent & the Grenadines',
                    link: '/forex-brokerage-St-vincent-grenadines',
                },
                {
                    name: 'Brokerage License – Nauru',
                    link: '/brokerage-license-nauru',
                },
            ],
        },

        {
            id: 'banking',
            name: 'Banking License',
            links: [
                {name: 'Banking License', link: '/banking-license'},
                {
                    name: 'Banking License – Malta (MFSA/ECB)',
                    link: '/banking-license-malta',
                },
                {
                    name: 'Banking License – Cyprus',
                    link: '/banking-license-cyprus',
                },
                {
                    name: 'Banking License – Panama',
                    link: '/banking-license-panama',
                },
                {
                    name: 'Banking License – Seychelles',
                    link: '/banking-license-seychelles',
                },
                {
                    name: 'Banking License – Mauritius',
                    link: '/banking-license-mauritius',
                },
                {
                    name: 'Banking License – Saint Lucia',
                    link: '/banking-license-saint-lucia',
                },
                {
                    name: 'Western Sahara Banking License',
                    link: '/western-sahara-banking-license',
                },
                {
                    name: 'Banking License – Mwali (Comoros)',
                    link: '/banking-license-mwali',
                },
                {
                    name: 'Banking License – Nauru',
                    link: '/banking-license-nauru',
                },
                {
                    name: 'Isle of Man Banking & FinTech License',
                    link: '/isle-man-banking-fintech-license',
                },
                {
                    name: 'Financial License – Anjouan (Comoros)',
                    link: '/financial-license-anjouan',
                },
            ],
        },
        {
            id: 'crypto',
            name: 'Crypto & Blockchain',
            links: [
                {name: 'Crypto & Virtual Asset Licenses', link: '/crypto-virtual-asset-licenses'},
                {
                    name: 'Crypto License – Western Sahara',
                    link: '/crypto-license-western-sahara',
                },
                {
                    name: 'Crypto License – Mwali',
                    link: '/crypto-license-mwali',
                },
                {
                    name: 'Crypto License – Anjouan',
                    link: '/crypto-license-anjouan',
                },
                {
                    name: 'Gibraltar Crypto & DLT License',
                    link: '/gibraltar-crypto-dlt-license',
                },
                {
                    name: 'Crypto & Blockchain License – Malta (VFA Act)',
                    link: '/crypto-blockchain-license-malta',
                },
                {
                    name: 'Crypto & Blockchain License – Cyprus (CySEC CASP)',
                    link: '/crypto-blockchain-license-cyprus',
                },
                {
                    name: 'Crypto License – Panama',
                    link: '/crypto-license-panama',
                },
                {
                    name: 'Crypto & Digital Asset License – Seychelles',
                    link: '/crypto-digital-asset-license-seychelles',
                },
                {
                    name: 'Crypto & VASP License – Mauritius',
                    link: '/Crypto-vasp-license-mauritius',
                },
                {
                    name: 'Crypto License – Nauru',
                    link: '/crypto-license-nauru',
                },
            ],
        },
        {
            id: 'trust',
            name: 'Trust & Insurance Licenses',
            links: [
                {name: 'Trust & Insurance Licenses', link: '/trust-insurance-licenses'},
                {
                    name: 'Trust & Insurance Licenses - Western Sahara',
                    link: '/trust-insurance-licenses-western-sahara',
                },
                {
                    name: 'Trust & Insurance Licenses Mwali (Comoros)',
                    link: '/trust-insurance-licenses-mwali',
                },
                {
                    name: 'Trust & Insurance Licenses Anjouan (Comoros)',
                    link: '/trust-insurance-licenses-anjouan',
                },
                {
                    name: 'Gibraltar Insurance & Reinsurance License',
                    link: '/gibraltar-insurance-reinsurance-license',
                },
                {
                    name: 'Insurance & Reinsurance License – Malta (MFSA)',
                    link: '/insurance-reinsurance-license-malta',
                },
                {
                    name: 'Insurance & Reinsurance License – Cyprus',
                    link: '/insurance-reinsurance-cyprus',
                },
                {
                    name: 'Insurance & Reinsurance License – Panama',
                    link: '/insurance-reinsurance-license-panama',
                },
                {
                    name: 'Insurance & Captives – Isle of Man',
                    link: '/insurance-captives-isle-man',
                },
                {
                    name: 'Insurance & Reinsurance License – Mauritius',
                    link: '/insurance-reinsurance-license-mauritius',
                },
                {
                    name: 'Insurance & Reinsurance License – Saint Lucia',
                    link: '/insurance-reinsurance-license-saint-lucia',
                },
                {
                    name: 'Insurance & Reinsurance License – Seychelles',
                    link: '/insurance-reinsurance-license-seychelles',
                },
                {
                    name: 'Trust Company License – Panama',
                    link: '/trust-company-license-panama',
                },
            ],
        },
        {
            id: 'fund',
            name: 'Fund & Asset Management',
            links: [
                {
                    name: 'Gibraltar Funds & Asset Management License',
                    link: '/gibraltar-funds-asset-management-license',
                },
                {name: 'Funds & Asset Management – Isle of Man', link: '/funds-asset-management-isle-man'},
                {
                    name: 'Funds & Asset Management License – Malta (MFSA)',
                    link: '/funds-asset-management-license-malta',
                },
                {
                    name: 'Funds & Asset Management License – Cyprus (CySEC)',
                    link: '/funds-asset-management-license-cyprus',
                },
                {
                    name: 'Funds & Asset Management License – Panama',
                    link: '/funds-asset-management-license-panama',
                },
                {
                    name: 'Asset Management License – Mwali (Comoros)',
                    link: '/asset-management-license-mwali',
                },
                {
                    name: 'Funds & Asset Management License – Mauritius',
                    link: '/funds-asset-management-license-mauritius',
                },
                {
                    name: 'Asset Management License – Western Sahara',
                    link: '/asset-management-license-western-sahara',
                },
            ],
        },
        {
            id: 'payment',
            name: 'Payment & FinTech',
            links: [
                {name: 'Payments & FinTech Licenses', link: '/payments-fintech-licenses'},
                {
                    name: 'Gibraltar Banking & FinTech License',
                    link: '/gibraltar-banking-finTech-license',
                },
                {
                    name: 'Payment Services & EMI License – Malta (MFSA)',
                    link: '/payment-services-EMI-license-malta',
                },
                {
                    name: 'Payment Services & EMI License – Cyprus (Central Bank of Cyprus)',
                    link: '/payment-services-EMI-license-cyrus',
                },
                {
                    name: 'Payments & FinTech License – Mauritius',
                    link: '/payments-fintech-license-mauritius',
                },
                {
                    name: 'Payments & FinTech License – Seychelles',
                    link: '/payments-fintech-license-seychelles',
                },
                {
                    name: 'FinTech & Payments License – Panama',
                    link: '/payments-fintech-license-panama',
                },
            ],
        },
        {
            id: 'areas',
            name: 'Areas',
            links: [
                {name: 'Offshore Company Registration', link: '/offshore-company-formation'},
                {name: 'Licensing', link: '/financial-license'},
                {
                    name: 'Opening bank accounts',
                    link: '/offshore-bank-account-opening',
                },
                {
                    name: 'Residency and Passport',
                    link: '/residency-passport',
                },
                {
                    name: 'Asset Management and Due Diligence',
                    link: '/assets-protection',
                },
                {name: 'Ready-Made Companies', link: '/ready-made'},
                {
                    name: 'Ready-Made Companies – Western Sahara',
                    link: '/ready-made-companies-western-sahara',
                },
                {name: 'Fintech Services', link: '/fintech-services'},
                {
                    name: 'Andorra Car License',
                    link: '/andorra-car-license',
                },
                {
                    name: 'Canadian MSB License',
                    link: '/canadian-msb-license',
                },

            ],
        },
        {
            id: 'company-sale',
            name: 'Company Sale',
            links: [
                {
                    name: 'Netherlands Company for Sale',
                    link: '/netherlands-company-sale'
                },
            ],
        },

    ];

    // Данные для категорий Jurisdictions
    const jurisdictionsCategories = [
        {
            id: 'jurisdictions',
            name: 'jurisdiction',
            links: [
                {
                    name: 'Western Sahara Jurisdiction',
                    link: '/western-sahara-jurisdiction',
                },
                {
                    name: 'Mwali Jurisdiction',
                    link: '/mwali-jurisdiction',
                },
                {
                    name: 'Anjouan Jurisdiction',
                    link: '/anjouan-jurisdiction',
                },
                {
                    name: 'Isle of Man as a Jurisdiction',
                    link: '/isle-man-jurisdiction',
                },
                {
                    name: 'Malta Jurisdiction',
                    link: '/malta-jurisdiction',
                },
                {
                    name: 'Cyprus Jurisdiction',
                    link: '/cyprus-jurisdiction',
                },
                {
                    name: 'Panama Jurisdiction',
                    link: '/panama-jurisdiction',
                },
                {
                    name: 'Seychelles Jurisdiction',
                    link: '/seychelles-jurisdiction',
                },
                {
                    name: 'Mauritius Jurisdiction',
                    link: '/mauritius-jurisdiction',
                },
                {
                    name: 'Curaçao Jurisdiction',
                    link: '/curaçao-jurisdiction',
                },
                {
                    name: 'St. Vincent & the Grenadines (SVG) as a Jurisdiction',
                    link: '/st-vincent-jurisdiction',
                },
                {
                    name: 'United Arab Emirates (UAE) Jurisdiction',
                    link: '/united-arab-emirates-jurisdiction',
                },
                {
                    name: 'United Kingdom Jurisdiction',
                    link: '/united-kingdom-jurisdiction',
                },
                {
                    name: 'Singapore Jurisdiction',
                    link: '/singapore-jurisdiction',
                },
                {
                    name: 'Indonesia Jurisdiction',
                    link: '/indonesia-jurisdiction',
                },
                {
                    name: 'Greece Jurisdiction',
                    link: '/greece-jurisdiction',
                },
                {
                    name: 'Spain Jurisdiction',
                    link: '/spain-jurisdiction',
                },
                {
                    name: 'Italy Jurisdiction',
                    link: '/italy-jurisdiction',
                },
                {
                    name: 'Dominica Jurisdiction',
                    link: '/dominica-jurisdiction',
                },
                {
                    name: 'Maritime & Shipping Licenses – Panama',
                    link: '/maritime-shipping-license-panama',
                },
            ],
        },
        {
            id: 'company-formation',
            name: 'Company Formation',
            links: [
                {
                    name: 'Offshore company-formation',
                    link: '/offshore-company-formation',
                },
                {
                    name: 'Western Sahara IBC (International Business Company)',
                    link: '/western-sahara-ibc',
                },
                {
                    name: 'Mwali IBC (International Business Company)',
                    link: '/mwali-ibc',
                },
                {
                    name: 'Anjouan IBC (International Business Company)',
                    link: '/anjouan-ibc',
                },
                {
                    name: 'Cyprus company-formation',
                    link: '/cyprus-company-formation',
                },
                {
                    name: 'St. Lucia company-formation',
                    link: '/st-lucia-company-formation',
                },
                {
                    name: 'UK company-formation',
                    link: '/uk-company-formation',
                },
                {
                    name: 'Indonesia company-formation (PT PMA)',
                    link: '/indonesia-company-formation',
                },
                {
                    name: 'Panama company-formation',
                    link: '/panama-company-formation',
                },
                {
                    name: 'Malta company-formation',
                    link: '/malta-company-formation',
                },
                {
                    name: 'Mauritius company-formation',
                    link: '/mauritius-company-formation',
                },
                {
                    name: 'Isle of Man company-formation',
                    link: '/isle-man-company-formation',
                },
                {
                    name: 'St. Vincent company-formation',
                    link: '/st-vincent-company-formation',
                },
                {
                    name: 'UAE company-formation',
                    link: '/uae--company-formation',
                },
                {
                    name: 'Singapore company-formation',
                    link: '/singapore-company-formation',
                },
                {
                    name: 'Hong Kong Offshore company-formation',
                    link: '/hong-kong-offshore-company',
                },
                {
                    name: 'Singapore Offshore company-formation',
                    link: '/singapore-offshore-company',
                },
                {
                    name: 'company-formation in Seychelles',
                    link: '/seychelles-company-formation',
                },
                {
                    name: 'company-formation in Dominica',
                    link: '/dominica-company-formation',
                },
                {
                    name: 'Residency & Investment – Gibraltar',
                    link: '/company-formation-gibraltar',
                },
                {
                    name: 'Anjouan Offshore company-formation',
                    link: '/anjouan-jurisdiction',
                },
                {
                    name: 'Western Sahara Offshore company-formation',
                    link: '/western-sahara-jurisdiction',
                },
                {
                    name: 'Cyprus Offshore company-formation',
                    link: '/cyprus-jurisdiction',
                },
                {
                    name: 'Andorra Company Formation',
                    link: '/company-formation-andorra',
                },
            ],
        },
        {
            id: 'residency',
            name: 'Residency & Passport',
            links: [
                {
                    name: 'Residency and Passport',
                    link: '/residency-passport',
                },
                {
                    name: 'Residency & Investment UAE',
                    link: '/residency-investment-uae',
                },
                {name: 'Golden visa Spain', link: '/golden-visa-spain'},
                {name: 'Golden visa Italy', link: '/golden-visa-italy'},
                {name: 'Residency Indonesia', link: '/residency-indonesia'},
                {
                    name: 'Residency & Investment Cyprus',
                    link: '/residency-investment-cyprus',
                },
                {
                    name: 'Residency Citizenship Dominica',
                    link: '/residency-citizenship-dominica',
                },
                {
                    name: 'Residency & Investment Gibraltar',
                    link: '/residency-investment-gibraltar',
                },
                {
                    name: 'Residency & Investment – Isle of Man',
                    link: '/residency-investment-isle-man',
                },
                {
                    name: 'Residency & Citizenship by Investment – Saint Lucia',
                    link: '/residency-citizenship-investment-saint-lucia',
                },
                {
                    name: 'Andorra Residency and Golden Visa',
                    link: '/andorra-residency-golden-visa',
                },
                {
                    name: 'Diplomatic Passport – Papua New Guinea',
                    link: '/diplomatic-passport-papua-new-guinea',
                },
                {
                    name: 'Turkey Residency & Company Setup',
                    link: '/turkey-residency',
                },
            ],
        },
        {
            id: 'investment',
            name: 'Real Estate Investment',
            links: [
                {
                    name: 'Real Estate Investment',
                    link: '/real-estate-investment',
                },
                {
                    name: 'Real Estate Investment In UAE',
                    link: '/real-estate-investment-uae',
                },
                {name: 'Real Estate Investment in Bali, Indonesia', link: '/real-estate-investment-bali-indonesia'},
                {name: 'Real Estate Investment in Spain', link: '/real-estate-investment-spain'},
                {name: 'Real Estate Investment in Cyprus', link: '/real-estate-investment-cyprus'},
                {name: 'Real Estate Investment in Italy', link: '/real-estate-investment-italy'},
                {
                    name: 'Real Estate Investment in Greece',
                    link: '/real-estate-investment-greece',
                },
            ],
        },
        {
            id: 'tax',
            name: 'Non-Dom Tax Regime',
            links: [
                {
                    name: 'UK Non-Domiciled (Non-Dom) Tax Regime',
                    link: '/uk-non-domiciled-tax-regime',
                },
                {
                    name: 'Non Dom Tax Regime',
                    link: '/non-dom-tax-regime',
                },
                {name: 'Italy Non Dom Tax Regime', link: '/italy-non-dom-tax-regime'},
            ],
        },
        {
            id: 'advisory',
            name: 'Financial & Advisory',
            links: [
                {
                    name: 'Offshore bank account set up',
                    link: '/offshore-bank-account-opening',
                },
                {
                    name: 'FinTech Services',
                    link: '/fintech-services',
                },
                {name: 'Financial License', link: '/financial-license'},
                {name: 'Ready Made Companies', link: '/ready-made'},
                {
                    name: 'Ready-Made Companies with Corporate Bank Accounts',
                    link: '/ready-made-companies-corporate-bank-accounts'
                },
                {name: 'Assets Protection', link: '/assets-protection'},
            ],
        },
    ];

    const aboutLinks = [
        {name: 'About the Company', link: '/about'},
        {name: 'FAQ', link: '/faq'},
        {name: 'Why Offshore Advisory', link: '/why-offshore-advisory'},
        {name: 'Client Testimonials', link: '/client-testimonials'},
    ];

    // Wrap each nav name with its shared translation key; links stay as-is.
    // The English name is passed as the fallback, so untranslated locales
    // (e.g. zh before it is filled in) still render readable English.
    const translateCategories = (cats, scope) =>
        cats.map((c) => ({
            ...c,
            name: t(`nav.${scope}.${c.id}.title`, c.name),
            links: c.links.map((l) => ({
                ...l,
                name: t(`nav.${scope}.${c.id}.items.${l.link}`, l.name),
            })),
        }));

    const tServicesCategories = translateCategories(servicesCategories, 'services');
    const tJurisdictionsCategories = translateCategories(jurisdictionsCategories, 'jurisdictions');
    const translatedAboutLinks = aboutLinks.map((l) => ({
        ...l,
        name: t(`nav.about.${l.link}`, l.name),
    }));

    // Flatten each top-level menu's links so a parent button / category tab can be
    // marked active when the current route lives under it.
    const servicesAllLinks = tServicesCategories.flatMap((c) => c.links);
    const jurisdictionsAllLinks = tJurisdictionsCategories.flatMap((c) => c.links);
    const aboutActive = isAnyActive(translatedAboutLinks);
    const servicesActive = isAnyActive(servicesAllLinks);
    const jurisdictionsActive = isAnyActive(jurisdictionsAllLinks);
    const categoryHasActive = (category) => isAnyActive(category.links);

    // Keep the "open on the active category" refs in sync with the current route.
    servicesActiveCatRef.current =
        (tServicesCategories.find(categoryHasActive) || {}).id || 'gaming-license';
    jurisdictionsActiveCatRef.current =
        (tJurisdictionsCategories.find(categoryHasActive) || {}).id || 'jurisdictions';

    // Рендеринг простого дропдауна (десктоп)
    const renderSimpleDropdown = (links, title = null) => (
        <div className="dropdown-content simple-dropdown">
            <div className="dropdown-group">
                {title && <span className="dropdown-title">{title}</span>}
                <ul className="dropdown-list">
                    {links.map((item, index) => (
                        <li key={index}>
                            <Link
                                to={lp(item.link)}
                                className={`dropdown-link ${isLinkActive(item.link) ? 'active' : ''}`}
                                aria-current={isLinkActive(item.link) ? 'page' : undefined}
                                onClick={scrollToTopAndCloseMenu}
                            >
                                {item.name}
                            </Link>
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );

    // Рендеринг сложного дропдауна (десктоп)
    const renderComplexDropdown = (type) => {
        const categories =
            type === 'services' ? tServicesCategories : tJurisdictionsCategories;
        const currentCategory =
            categories.find((cat) => cat.id === activeCategory) || categories[0];

        return (
            <div className="dropdown-content complex-dropdown">
                <div className="dropdown-navigation">
                    <div className="nav-categories">
                        {categories.map((category) => (
                            <button
                                key={category.id}
                                className={`nav-category ${
                                    activeCategory === category.id ? 'active' : ''
                                } ${categoryHasActive(category) ? 'has-active' : ''}`}
                                onClick={() => setActiveCategory(category.id)}
                                type="button"
                            >
                                {category.name}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="dropdown-content-container">
                    <div className="dropdown-group">
                        <span className="dropdown-title">{currentCategory.name}</span>
                        <ul className="dropdown-list">
                            {currentCategory.links.map((item, index) => (
                                <li key={index}>
                                    <Link
                                        to={lp(item.link)}
                                        className={`dropdown-link ${isLinkActive(item.link) ? 'active' : ''}`}
                                        aria-current={isLinkActive(item.link) ? 'page' : undefined}
                                        onClick={scrollToTopAndCloseMenu}
                                    >
                                        {item.name}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        );
    };

    // Навигация для десктопа
    const renderDesktopNav = () => (
        <ul className="nav-list">
            {/* about-company */}
            <li
                className={`dropdown-item ${
                    openDropdown === 'aboutcompany' ? 'show-dropdown' : ''
                }`}
            >
                <div
                    className={`nav-link dropdown-button ${aboutActive ? 'active-parent' : ''}`}
                    onClick={() => toggleDropdown('aboutcompany')}
                    role="button"
                    tabIndex={0}
                >
                    {t('header.buttons.about')} {renderDropdownArrow(openDropdown === 'aboutcompany')}
                </div>
                {openDropdown === 'aboutcompany' && (
                    <div className="dropdown-container">
                        {renderSimpleDropdown(translatedAboutLinks)}
                    </div>
                )}
            </li>

            {/* Services */}
            <li
                className={`dropdown-item ${
                    openDropdown === 'services' ? 'show-dropdown' : ''
                }`}
            >
                <div
                    className={`nav-link dropdown-button ${servicesActive ? 'active-parent' : ''}`}
                    onClick={() => toggleDropdown('services')}
                    role="button"
                    tabIndex={0}
                >
                    {t('header.buttons.services')} {renderDropdownArrow(openDropdown === 'services')}
                </div>
                {openDropdown === 'services' && (
                    <div className="dropdown-container wide-dropdown">
                        {renderComplexDropdown('services')}
                    </div>
                )}
            </li>

            {/* Jurisdictions */}
            <li
                className={`dropdown-item ${
                    openDropdown === 'jurisdictions' ? 'show-dropdown' : ''
                }`}
            >
                <div
                    className={`nav-link dropdown-button ${jurisdictionsActive ? 'active-parent' : ''}`}
                    onClick={() => toggleDropdown('jurisdictions')}
                    role="button"
                    tabIndex={0}
                >
                    {t('header.buttons.offshoreStructures')} {renderDropdownArrow(openDropdown === 'jurisdictions')}
                </div>
                {openDropdown === 'jurisdictions' && (
                    <div className="dropdown-container wide-dropdown">
                        {renderComplexDropdown('jurisdictions')}
                    </div>
                )}
            </li>

            {/* Простые ссылки */}
            <li>
                <Link
                    to={lp("/contact")}
                    className={`nav-link ${isLinkActive('/contact') ? 'active' : ''}`}
                    aria-current={isLinkActive('/contact') ? 'page' : undefined}
                    onClick={scrollToTopAndCloseMenu}
                >
                    {t('header.buttons.contact')}
                </Link>
            </li>

            <li>
                <Link
                    to={lp("/blogs")}
                    className={`nav-link ${isLinkActive('/blogs') ? 'active' : ''}`}
                    aria-current={isLinkActive('/blogs') ? 'page' : undefined}
                    onClick={scrollToTopAndCloseMenu}
                >
                    {t('header.buttons.blogs')}
                </Link>
            </li>

            <li>
                <Link
                    to={lp("/newsletter")}
                    className="subscribe-link"
                    onClick={scrollToTopAndCloseMenu}
                    style={{width: 'max-content'}}
                >
                    {t('header.buttons.subscribe')}
                </Link>
            </li>

            <li className="lang-switch-item">
                <LanguageSwitcher variant="header"/>
            </li>
        </ul>
    );

    // Навигация для мобильных: те же пункты, но аккордеоны
    const renderMobileNav = () => {
        const toggleSection = (sectionKey) => {
            setOpenMobileSection((prev) =>
                prev === sectionKey ? null : sectionKey
            );
            setOpenMobileSubcategory(null);
        };

        const toggleSubcategory = (subcategoryId) => {
            setOpenMobileSubcategory((prev) =>
                prev === subcategoryId ? null : subcategoryId
            );
        };

        return (
            <ul className="nav-list mobile-nav">
                {/* about-company */}
                <li
                    className={`mobile-dropdown ${
                        openMobileSection === 'aboutcompany' ? 'open' : ''
                    }`}
                >
                    <button
                        type="button"
                        className={`mobile-dropdown-toggle ${aboutActive ? 'active-parent' : ''}`}
                        onClick={() => toggleSection('aboutcompany')}
                    >
                        <span>{t('header.buttons.about')}</span>
                        {renderDropdownArrow(openMobileSection === 'aboutcompany')}
                    </button>
                    {openMobileSection === 'aboutcompany' && (
                        <div className="mobile-dropdown-content">
                            {translatedAboutLinks.map((item) => (
                                <Link
                                    key={item.link}
                                    to={lp(item.link)}
                                    className={`mobile-nav-link mobile-nav-link-inner ${isLinkActive(item.link) ? 'active' : ''}`}
                                    aria-current={isLinkActive(item.link) ? 'page' : undefined}
                                    onClick={scrollToTopAndCloseMenu}
                                >
                                    {item.name}
                                </Link>
                            ))}
                        </div>
                    )}
                </li>

                {/* Services (сложное меню: категории + ссылки) */}
                <li
                    className={`mobile-dropdown ${
                        openMobileSection === 'services' ? 'open' : ''
                    }`}
                >
                    <button
                        type="button"
                        className={`mobile-dropdown-toggle ${servicesActive ? 'active-parent' : ''}`}
                        onClick={() => toggleSection('services')}
                    >
                        <span>{t('header.buttons.services')}</span>
                        {renderDropdownArrow(openMobileSection === 'services')}
                    </button>
                    {openMobileSection === 'services' && (
                        <div className="mobile-dropdown-content">
                            {tServicesCategories.map((category) => (
                                <div
                                    key={category.id}
                                    className={`mobile-subcategory ${
                                        openMobileSubcategory === category.id ? 'open' : ''
                                    }`}
                                >
                                    <button
                                        type="button"
                                        className={`mobile-subcategory-toggle ${categoryHasActive(category) ? 'has-active' : ''}`}
                                        onClick={() => toggleSubcategory(category.id)}
                                    >
                                        <span>{category.name}</span>
                                        {renderDropdownArrow(
                                            openMobileSubcategory === category.id
                                        )}
                                    </button>
                                    {openMobileSubcategory === category.id && (
                                        <ul className="mobile-subcategory-list">
                                            {category.links.map((item) => (
                                                <li key={item.link}>
                                                    <Link
                                                        to={lp(item.link)}
                                                        className={`mobile-nav-link mobile-nav-link-deep ${isLinkActive(item.link) ? 'active' : ''}`}
                                                        aria-current={isLinkActive(item.link) ? 'page' : undefined}
                                                        onClick={scrollToTopAndCloseMenu}
                                                    >
                                                        {item.name}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    )}


                                </div>
                            ))}

                        </div>
                    )}
                </li>

                {/* Jurisdictions (сложное меню: категории + ссылки) */}
                <li
                    className={`mobile-dropdown ${
                        openMobileSection === 'jurisdictions' ? 'open' : ''
                    }`}
                >
                    <button
                        type="button"
                        className={`mobile-dropdown-toggle ${jurisdictionsActive ? 'active-parent' : ''}`}
                        onClick={() => toggleSection('jurisdictions')}
                    >
                        <span>{t('header.buttons.offshoreStructures')}</span>
                        {renderDropdownArrow(openMobileSection === 'jurisdictions')}
                    </button>
                    {openMobileSection === 'jurisdictions' && (
                        <div className="mobile-dropdown-content">
                            {tJurisdictionsCategories.map((category) => (
                                <div
                                    key={category.id}
                                    className={`mobile-subcategory ${
                                        openMobileSubcategory === category.id ? 'open' : ''
                                    }`}
                                >
                                    <button
                                        type="button"
                                        className={`mobile-subcategory-toggle ${categoryHasActive(category) ? 'has-active' : ''}`}
                                        onClick={() => toggleSubcategory(category.id)}
                                    >
                                        <span>{category.name}</span>
                                        {renderDropdownArrow(
                                            openMobileSubcategory === category.id
                                        )}
                                    </button>
                                    {openMobileSubcategory === category.id && (
                                        <ul className="mobile-subcategory-list">
                                            {category.links.map((item) => (
                                                <li key={item.link}>
                                                    <Link
                                                        to={lp(item.link)}
                                                        className={`mobile-nav-link mobile-nav-link-deep ${isLinkActive(item.link) ? 'active' : ''}`}
                                                        aria-current={isLinkActive(item.link) ? 'page' : undefined}
                                                        onClick={scrollToTopAndCloseMenu}
                                                    >
                                                        {item.name}
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </li>

                {/* Простые ссылки без подменю */}
                <li className="mobile-simple-item">
                    <Link
                        to={lp("/contact")}
                        className={`mobile-nav-link ${isLinkActive('/contact') ? 'active' : ''}`}
                        aria-current={isLinkActive('/contact') ? 'page' : undefined}
                        onClick={scrollToTopAndCloseMenu}
                    >
                        {t('header.buttons.contact')}
                    </Link>
                </li>

                <li className="mobile-simple-item">
                    <Link
                        to={lp("/blogs")}
                        className={`mobile-nav-link ${isLinkActive('/blogs') ? 'active' : ''}`}
                        aria-current={isLinkActive('/blogs') ? 'page' : undefined}
                        onClick={scrollToTopAndCloseMenu}
                    >
                        {t('header.buttons.blogs')}
                    </Link>
                </li>

                <li className="mobile-simple-item">
                    <Link
                        to={lp("/newsletter")}
                        className="subscribe-link mobile-subscribe"
                        onClick={scrollToTopAndCloseMenu}
                    >
                        {t('header.buttons.subscribe')}
                    </Link>
                </li>

                <li className="mobile-simple-item mobile-lang-item">
                    <LanguageSwitcher variant="mobile"/>
                </li>

            </ul>
        );
    };

    return (
        <header className="header" ref={headerRef}>
            <nav className="nav container">
                <div className="nav-data">
                    <Link to={lp("/")} className="nav-logo" onClick={closeAll}>
                        <img
                            src={HeaderLogo}
                            alt={t('header.aria.logoAlt')}
                            className="header-logo-img"
                        />
                        {t('header.brand')}
                    </Link>

                    {/* На мобильных: бургер в шапке (переключатель языка — в меню, под Subscribe) */}
                    {isMobile && (
                        <div className="mobile-header-actions">
                            <button
                                type="button"
                                className="nav-toggle"
                                onClick={toggleMenu}
                                aria-label={menuOpen ? t('header.aria.closeMenu') : t('header.aria.openMenu')}
                                aria-expanded={menuOpen}
                            >
                                {menuOpen ? (
                                    <RiCloseLine className="nav-toggle-close"/>
                                ) : (
                                    <RiMenuLine className="nav-toggle-menu"/>
                                )}
                            </button>
                        </div>
                    )}
                </div>

                {/* Навигация для десктопа */}
                {!isMobile && (
                    <div className="nav-menu desktop-nav">{renderDesktopNav()}</div>
                )}

                {/* Мобильное меню */}
                {isMobile && (
                    <div className={`mobile-menu ${menuOpen ? 'show-menu' : ''}`}>
                        {renderMobileNav()}
                    </div>
                )}
            </nav>
        </header>
    );
};

export default Header;
